import { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const colorSequence = [
  { bg: '--kell-violet',    text: '#FFFFFF' },
  { bg: '--kell-gold',      text: '#000000' },
  { bg: '--kell-raspberry', text: '#FFFFFF' },
  { bg: '--kell-cyan',      text: '#000000' },
  { bg: '--kell-raspberry', text: '#FFD000' },
];

export default function App() {
  const [phase, setPhase] = useState('slide-in');
  const [colorIndex, setColorIndex] = useState(0);
  const [kellRect, setKellRect] = useState(null);
  const kellRef = useRef(null);

  const measureKell = useCallback(() => {
    if (!kellRef.current) return;
    const r = kellRef.current.getBoundingClientRect();
    setKellRect({
      left: r.left,
      width: r.width,
    });
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('full'), 100);
    const t2 = setTimeout(() => {
      measureKell();
      setPhase('shrink');
    }, 1400);
    const t3 = setTimeout(() => setPhase('footer'), 2600);
    const t4 = setTimeout(() => setPhase('settled'), 3400);
    const t5 = setTimeout(() => setPhase('cycling'), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, [measureKell]);

  useEffect(() => {
    if (phase !== 'cycling') return;
    if (colorIndex >= colorSequence.length - 1) return;
    const id = setTimeout(() => setColorIndex(prev => prev + 1), 2000);
    return () => clearTimeout(id);
  }, [phase, colorIndex]);

  useEffect(() => {
    const handleResize = () => measureKell();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [measureKell]);

  const current = colorSequence[colorIndex];
  const showOverlay = phase === 'slide-in' || phase === 'full' || phase === 'shrink';
  const isLanded = phase === 'footer' || phase === 'settled' || phase === 'cycling';
  const footerVisible = phase === 'footer' || phase === 'settled' || phase === 'cycling';
  const textVisible = phase !== 'slide-in';

  // Intro overlay
  let overlayStyle = {
    position: 'fixed',
    backgroundColor: `var(${current.bg})`,
    transition: 'all 1.2s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.8s ease-in-out',
    zIndex: 10,
  };

  if (phase === 'slide-in') {
    Object.assign(overlayStyle, { top: 0, left: 0, width: '100vw', height: '100vh', transform: 'translateX(-100%)' });
  } else if (phase === 'full') {
    Object.assign(overlayStyle, { top: 0, left: 0, width: '100vw', height: '100vh', transform: 'translateX(0)' });
  } else if (phase === 'shrink' && kellRect) {
    Object.assign(overlayStyle, { top: 0, left: kellRect.left, width: kellRect.width, height: '100vh' });
  }

  return (
    <div style={{
      width: '100vw', height: '100vh', backgroundColor: '#FFFFFF',
      position: 'relative', overflow: 'hidden',
    }}>

      {/* Intro sliding overlay */}
      {showOverlay && <div style={overlayStyle} />}

      {/* Settled color bar — separate from text, UNDER the footer */}
      {isLanded && kellRect && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: kellRect.left,
          width: kellRect.width,
          height: '100vh',
          backgroundColor: `var(${current.bg})`,
          transition: 'background-color 0.8s ease-in-out',
          zIndex: 15,
        }} />
      )}

      {/* Black footer — slides up, ON TOP of color bar */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, width: '100vw',
        height: '30vh',
        backgroundColor: '#000000',
        transform: footerVisible ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1)',
        zIndex: 25,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        padding: '2vh 8.5vw 0',
      }}>
        <div style={{ textAlign: 'right' }}>
          <h2 style={{
            fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 400,
            color: '#FFFFFF', letterSpacing: '0.02em',
            fontFamily: "'ZT Nature', 'Inter', sans-serif",
            margin: 0,
            opacity: footerVisible ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out 0.4s',
          }}>
            Custom Business Systems and Technology
          </h2>
          <p style={{
            fontSize: 'clamp(20px, 2.5vw, 32px)', fontWeight: 400,
            color: '#FFFFFF', letterSpacing: '0.02em',
            fontFamily: "'ZT Nature', 'Inter', sans-serif",
            margin: '4px 0 0',
            opacity: footerVisible ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out 0.6s',
          }}>
            hello@thekell.co
          </p>
        </div>
      </div>

      {/* Text — sits just above the footer */}
      <div style={{
        position: 'absolute', right: '8vw', bottom: '32vh',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
        zIndex: 30,
      }}>
        <h1 style={{
          fontSize: 'clamp(48px, 8vw, 120px)', fontWeight: 800,
          letterSpacing: '0.02em', margin: 0, lineHeight: 1,
          fontFamily: "'ZT Nature', 'Inter', sans-serif",
          opacity: textVisible ? 1 : 0,
          transition: 'opacity 0.6s ease-in-out',
        }}>
          <span style={{
            color: isLanded ? '#000000' : '#FFFFFF',
            transition: 'color 0.8s ease-in-out',
          }}>the</span>

          <span ref={kellRef} style={{ position: 'relative', display: 'inline-block' }}>
            <span style={{
              position: 'relative', zIndex: 31,
              color: isLanded ? current.text : '#FFFFFF',
              transition: 'color 0.8s ease-in-out',
            }}>kell</span>
          </span>

          <span style={{
            color: isLanded ? '#000000' : '#FFFFFF',
            transition: 'color 0.8s ease-in-out',
          }}>co</span>
        </h1>

        {/* Tagline — visible only during full violet screen */}
        <p style={{
          fontSize: 'clamp(18px, 2.5vw, 32px)', fontWeight: 400,
          color: '#FFFFFF', letterSpacing: '0.02em',
          fontFamily: "'ZT Nature', 'Inter', sans-serif",
          margin: '16px 0 0',
          opacity: phase === 'full' ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
          pointerEvents: 'none',
        }}>
          Technology for Everyone
        </p>
      </div>
    </div>
  );
}
